import React, { useState } from "react";
import { calculateRelevanceFromScore } from "../utils";

// Download icon for export button
const DownloadIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7,10 12,15 17,10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>
	</svg>
);

// Chevron down icon for dropdown
const ChevronDownIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="6,9 12,15 18,9"></polyline>
	</svg>
);

// Close icon
const XIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="18" y1="6" x2="6" y2="18"></line>
		<line x1="6" y1="6" x2="18" y2="18"></line>
	</svg>
);

// Eye icon for viewing domain details
const EyeIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
		<circle cx="12" cy="12" r="3"></circle>
	</svg>
);

// Check icon for geographic coverage
const CheckIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="20,6 9,17 4,12"></polyline>
	</svg>
);

// Filter icon
const FilterIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"></polygon>
	</svg>
);

// Helper function to format reach values - convert from millions to raw numbers and round
const formatReachValue = (reach) => {
	if (reach === null || reach === undefined || reach === "") return "N/A";

	// If it's already a number, round it
	if (typeof reach === "number") {
		return Math.round(reach).toLocaleString();
	}

	// If it's a string, check if it has 'M' or 'K' suffix
	if (typeof reach === "string") {
		const cleanReach = reach.toString().toLowerCase();

		if (cleanReach.includes("m")) {
			// Convert from millions to raw number
			const numValue = parseFloat(cleanReach.replace("m", ""));
			return Math.round(numValue * 1000000).toLocaleString();
		} else if (cleanReach.includes("k")) {
			// Convert from thousands to raw number
			const numValue = parseFloat(cleanReach.replace("k", ""));
			return Math.round(numValue * 1000).toLocaleString();
		} else if (!isNaN(reach)) {
			// It's a numeric string
			return Math.round(parseFloat(reach)).toLocaleString();
		}
	}

	return reach;
};

// Export Dropdown Component
const ExportDropdown = ({ setData }) => {
	const [isOpen, setIsOpen] = useState(false);

	const exportSummarized = () => {
		const headers = ["Domain", "Market Request"];
		const csvContent = [
			headers.join(","),
			...setData.domains.map((domain) => {
				const domainName =
					domain.name ||
					domain.domain ||
					domain.url ||
					domain.hostname ||
					domain;
				const marketRequest =
					domain.totalCprMarketRequests || domain.metrics?.marketRequests || 0;

				return [
					typeof domainName === "string" ? domainName : "Unknown Domain",
					marketRequest,
				].join(",");
			}),
		].join("\n");

		downloadCSV(
			csvContent,
			`${setData.name.replace(/\s+/g, "_")}_summarized.csv`,
		);
		setIsOpen(false);
	};

	const exportDetailed = () => {
		const headers = [
			"Domain",
			"Categories",
			"Market Request",
			"CTR",
			"Viewability",
			"CPM",
			"Publisher",
			"Reach",
			"Relevance",
			"VTR",
			"Completion Rate",
			"Clicks",
			"Impressions",
			"Impressions With Click Macro",
			"Total Viewables",
			"Total Viewability Measures",
			"Total Start First Occurrence",
			"Total Complete First Occurrence",
			"Total Bid Spend USD",
			"Domain Reach",
			"Score",
			"Avg Bid CPM",
		];
		const csvContent = [
			headers.join(","),
			...setData.domains.map((domain) => {
				const domainName =
					domain.name ||
					domain.domain ||
					domain.url ||
					domain.hostname ||
					domain;
				const domainMetrics = domain.metrics || domain;

				const categories = domainMetrics.categories || domain.categories || [];
				const categoriesString = Array.isArray(categories)
					? `"${categories.join(", ")}"`
					: `"${categories}"`;

				return [
					typeof domainName === "string" ? domainName : "Unknown Domain",
					categoriesString,
					domain.totalCprMarketRequests || domainMetrics.marketRequests || 0,
					domainMetrics.rawCtr || domainMetrics.ctr || 0,
					domainMetrics.viewability || 0,
					domainMetrics.cpm || 0,
					domainMetrics.publisher || domain.publisher || "N/A",
					formatReachValue(domainMetrics.reach || domain.reach) || 0,
					domainMetrics.relevance || domain.relevance || 0,
					domainMetrics.vtr || domain.vtr || 0,
					domainMetrics.completionRate || domain.completionRate || 0,
					domainMetrics.clicks || domain.clicks || 0,
					domainMetrics.impressions ||
						domain.impressions ||
						domainMetrics.totalImpressions ||
						0,
					domainMetrics.impressionsWithClickMacro ||
						domain.totalImpressionsWithClickMacro ||
						0,
					domainMetrics.totalViewables || domain.totalViewables || 0,
					domainMetrics.totalViewabilityMeasures ||
						domain.totalViewabilityMeasures ||
						0,
					domainMetrics.totalStartFirstOccurrence ||
						domain.totalStartFirstOccurrence ||
						0,
					domainMetrics.totalCompleteFirstOccurrence ||
						domain.totalCompleteFirstOccurrence ||
						0,
					domainMetrics.totalBidSpendInUsd || domain.totalBidSpendInUsd || 0,
					domainMetrics.domainReach || domain.domainReach || 0,
					domainMetrics.score || domain.score || 0,
					domainMetrics.avgBidCpm || domain.avgBidCpm || 0,
				].join(",");
			}),
		].join("\n");

		downloadCSV(
			csvContent,
			`${setData.name.replace(/\s+/g, "_")}_detailed.csv`,
		);
		setIsOpen(false);
	};
	const downloadCSV = (content, filename) => {
		const blob = new Blob([content], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.click();
		window.URL.revokeObjectURL(url);
	};

	return (
		<div className="export-dropdown" style={{ position: "relative" }}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="btn-medium-secondary"
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
				}}
			>
				<DownloadIcon />
				Export Set
				<ChevronDownIcon />
			</button>

			{isOpen && (
				<div
					className="dropdown-menu"
					style={{
						position: "absolute",
						top: "100%",
						right: 0,
						backgroundColor: "white",
						border: "1px solid var(--openx-border)",
						borderRadius: "4px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
						zIndex: 1000,
						minWidth: "160px",
						marginTop: "4px",
					}}
				>
					<button
						onClick={exportSummarized}
						className="dropdown-item"
						style={{
							width: "100%",
							padding: "8px 12px",
							border: "none",
							background: "none",
							textAlign: "left",
							cursor: "pointer",
							fontSize: "14px",
							color: "var(--openx-text-primary)",
						}}
						onMouseEnter={(e) =>
							(e.target.style.backgroundColor = "var(--openx-surface)")
						}
						onMouseLeave={(e) =>
							(e.target.style.backgroundColor = "transparent")
						}
					>
						Export Summarized
					</button>
					<button
						onClick={exportDetailed}
						className="dropdown-item"
						style={{
							width: "100%",
							padding: "8px 12px",
							border: "none",
							background: "none",
							textAlign: "left",
							cursor: "pointer",
							fontSize: "14px",
							color: "var(--openx-text-primary)",
						}}
						onMouseEnter={(e) =>
							(e.target.style.backgroundColor = "var(--openx-surface)")
						}
						onMouseLeave={(e) =>
							(e.target.style.backgroundColor = "transparent")
						}
					>
						Export Detailed
					</button>
				</div>
			)}
		</div>
	);
};

// Filter Dropdown Component
const FilterDropdown = ({ onFilterChange, currentFilter }) => {
	const [isOpen, setIsOpen] = useState(false);

	const filterOptions = [
		{ value: "all", label: "All Domains" },
		{ value: "high-ctr", label: "High CTR (>2%)" },
		{ value: "high-viewability", label: "High Viewability (>70%)" },
		{ value: "premium-cpm", label: "Premium CPM (>$5)" },
		{ value: "top-publishers", label: "Top Publishers" },
		{ value: "brand-safe", label: "Brand Safe Only" },
	];

	const handleFilterSelect = (filterValue) => {
		onFilterChange(filterValue);
		setIsOpen(false);
	};

	const currentFilterLabel =
		filterOptions.find((option) => option.value === currentFilter)?.label ||
		"All Domains";

	return (
		<div className="filter-dropdown" style={{ position: "relative" }}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="filter-button"
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
					padding: "8px 12px",
					border: "1px solid var(--openx-border)",
					borderRadius: "4px",
					backgroundColor: "white",
					cursor: "pointer",
					fontSize: "14px",
					color: "var(--openx-text-primary)",
				}}
			>
				<FilterIcon />
				{currentFilterLabel}
				<ChevronDownIcon />
			</button>

			{isOpen && (
				<div
					className="dropdown-menu"
					style={{
						position: "absolute",
						top: "100%",
						left: 0,
						backgroundColor: "white",
						border: "1px solid var(--openx-border)",
						borderRadius: "4px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
						zIndex: 1000,
						minWidth: "200px",
						marginTop: "4px",
					}}
				>
					{filterOptions.map((option) => (
						<button
							key={option.value}
							onClick={() => handleFilterSelect(option.value)}
							className="dropdown-item"
							style={{
								width: "100%",
								padding: "8px 12px",
								border: "none",
								background:
									currentFilter === option.value
										? "var(--openx-surface)"
										: "none",
								textAlign: "left",
								cursor: "pointer",
								fontSize: "14px",
								color: "var(--openx-text-primary)",
							}}
							onMouseEnter={(e) => {
								if (currentFilter !== option.value) {
									e.target.style.backgroundColor = "var(--openx-surface)";
								}
							}}
							onMouseLeave={(e) => {
								if (currentFilter !== option.value) {
									e.target.style.backgroundColor = "transparent";
								}
							}}
						>
							{option.label}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

// Tab Navigation Component
const TabNavigation = ({ activeTab, setActiveTab, tabs }) => {
	return (
		<div className="tab-navigation">
			{tabs.map((tab) => (
				<button
					key={tab.id}
					className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
					onClick={() => setActiveTab(tab.id)}
					style={{
						background: "none",
						border: "none",
						padding: "16px 24px",
						cursor: "pointer",
						fontSize: "14px",
						fontWeight: "500",
						color: activeTab === tab.id ? "#0076BD" : "#6b7280",
						borderBottom:
							activeTab === tab.id
								? "2px solid #0076BD"
								: "2px solid transparent",
						transition: "all 0.2s ease",
						whiteSpace: "nowrap",
					}}
				>
					{tab.label}
				</button>
			))}
		</div>
	);
};

// Overview Tab Component
// Fixed OverviewTab Component
const OverviewTab = ({ setData }) => {
	// Helper function to safely extract numeric values
	const extractNumericValue = (value, defaultValue = 0) => {
		if (value === null || value === undefined || value === "")
			return defaultValue;

		if (typeof value === "number") return value;

		if (typeof value === "string") {
			// Remove common suffixes and characters
			const cleaned = value
				.toString()
				.toLowerCase()
				.replace(/[%$,]/g, "")
				.replace(/[mk]/g, (match) => (match === "m" ? "000000" : "1000"));

			const parsed = parseFloat(cleaned);
			return isNaN(parsed) ? defaultValue : parsed;
		}

		return defaultValue;
	};

	// Extract data from domains with flexible field checking
	const extractedData = setData.domains.map((domain) => {
		const metrics = domain.metrics || {};

		// Check multiple possible locations for each metric
		const impressions = extractNumericValue(
			metrics.impressions ||
				domain.impressions ||
				metrics.totalImpressions ||
				domain.totalImpressions ||
				0,
		);

		const viewability = extractNumericValue(
			metrics.viewability ||
				domain.viewability ||
				metrics.avgViewability ||
				domain.avgViewability ||
				0,
		);

		const ctr = extractNumericValue(
			metrics.calculated_ctr || domain.calculated_ctr || 0,
		);

		const cpm = extractNumericValue(
			metrics.cpm ||
				domain.cpm ||
				metrics.calculated_costperimpression ||
				domain.calculated_costperimpression ||
				0,
		);

		const marketRequests = extractNumericValue(
			domain.totalCprMarketRequests ||
				metrics.marketRequests ||
				domain.marketRequests ||
				metrics.totalCprMarketRequests ||
				0,
		);

		return {
			domain: domain.name || domain.domain || "Unknown",
			impressions,
			viewability,
			ctr,
			cpm,
			marketRequests,
			originalDomain: domain,
		};
	});

	// Calculate aggregated metrics
	const totalDomains = setData.domains.length;
	const totalImpressions = extractedData.reduce(
		(sum, d) => sum + d.impressions,
		0,
	);
	const totalMarketRequests = extractedData.reduce(
		(sum, d) => sum + d.marketRequests,
		0,
	);

	// Calculate averages (only from domains that have data)
	const avgViewability = setData.metrics?.viewability?.value
		? parseFloat(setData.metrics.viewability.value.replace("%", ""))
		: 0;

	const avgCTR = setData.metrics?.ctr?.value
		? parseFloat(setData.metrics.ctr.value.replace("%", ""))
		: 0;

	const formatNumber = (num) => {
		if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
		if (num >= 1000) return (num / 1000).toFixed(1) + "K";
		return Math.round(num).toLocaleString();
	};

	return (
		<div className="overview-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: "32px",
					marginBottom: "24px",
				}}
			>
				<div className="overview-section" style={{ background: "white" }}>
					<h3
						style={{
							fontSize: "16px",
							fontWeight: "500",
							color: "#1f2937",
							margin: "0 0 20px 0",
							paddingBottom: "8px",
							borderBottom: "1px solid #e5e7eb",
						}}
					>
						Performance Metrics
					</h3>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr",
							gap: "12px",
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Market Requests
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{formatNumber(totalMarketRequests || totalImpressions * 0.3)}
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Average Viewability
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{avgViewability.toFixed(1)}%
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Average CTR
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{avgCTR.toFixed(2)}%
							</div>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								padding: "8px 0",
							}}
						>
							<div
								style={{
									fontSize: "12px",
									color: "#6b7280",
									textTransform: "uppercase",
									fontWeight: "400",
									letterSpacing: "0.5px",
								}}
							>
								Total Impressions
							</div>
							<div
								style={{
									fontSize: "15px",
									fontWeight: "600",
									color: "#1f2937",
								}}
							>
								{formatNumber(totalImpressions)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				style={{
					backgroundColor: "#f9fafb",
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
					padding: "16px",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-around",
						gap: "24px",
					}}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px",
						}}
					>
						<span
							style={{
								fontSize: "11px",
								color: "#6b7280",
								textTransform: "uppercase",
								fontWeight: "400",
							}}
						>
							Domains
						</span>
						<span
							style={{
								fontSize: "16px",
								fontWeight: "600",
								color: "#1f2937",
							}}
						>
							{totalDomains}
						</span>
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px",
						}}
					>
						<span
							style={{
								fontSize: "11px",
								color: "#6b7280",
								textTransform: "uppercase",
								fontWeight: "400",
							}}
						>
							Market Requests
						</span>
						<span
							style={{
								fontSize: "16px",
								fontWeight: "600",
								color: "#1f2937",
							}}
						>
							{formatNumber(totalMarketRequests || totalImpressions * 0.3)}
						</span>
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px",
						}}
					>
						<span
							style={{
								fontSize: "11px",
								color: "#6b7280",
								textTransform: "uppercase",
								fontWeight: "400",
							}}
						>
							Viewability
						</span>
						<span
							style={{
								fontSize: "16px",
								fontWeight: "600",
								color: "#1f2937",
							}}
						>
							{avgViewability.toFixed(1)}%
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

const DomainsTab = ({ setData, onViewDomain }) => {
	// Enhanced helper function to safely extract values and calculate relevance
	const extractDomainData = (domain) => {
		const metrics = domain.metrics || {};

		// Extract domain name
		const domainName =
			domain.name ||
			domain.domain ||
			domain.url ||
			domain.hostname ||
			domain.domainName ||
			`Domain ${Math.random().toString(36).substr(2, 9)}`;

		// Relevance uses the Domains sigmoid curve from the threshold sheet
		let relevance = 0;
		if (typeof domain.score === "number") {
			relevance = calculateRelevanceFromScore(domain.score);
		} else if (typeof domain.relevance === "number") {
			relevance = Math.round(domain.relevance);
		} else if (typeof domain.relevanceScore === "number") {
			relevance = Math.round(domain.relevanceScore);
		} else {
			relevance = 75;
		}

		// Extract CPM - Enhanced with multiple fallback strategies
		let cpm = "N/A";
		let cpmSource = "none";

		// Primary: domain.avgBidCpm (most reliable)
		if (
			domain.avgBidCpm !== undefined &&
			domain.avgBidCpm !== null &&
			domain.avgBidCpm !== 0
		) {
			cpm = `${(parseFloat(domain.avgBidCpm) / 1000000).toFixed(2)}`;
			cpmSource = "domain.avgBidCpm";
		}
		// Fallback 1: metrics.avgBidCpm
		else if (
			metrics.avgBidCpm !== undefined &&
			metrics.avgBidCpm !== null &&
			metrics.avgBidCpm !== 0
		) {
			cpm = `${(parseFloat(metrics.avgBidCpm) / 1000000).toFixed(2)}`;
			cpmSource = "metrics.avgBidCpm";
		}
		// Fallback 2: domain.calculated_costperimpression
		else if (
			domain.calculated_costperimpression !== undefined &&
			domain.calculated_costperimpression !== null &&
			domain.calculated_costperimpression !== 0
		) {
			cpm = `${(parseFloat(domain.calculated_costperimpression) / 1000000).toFixed(2)}`;
			cpmSource = "domain.calculated_costperimpression";
		}
		// Fallback 3: metrics.cpm
		else if (
			metrics.cpm !== undefined &&
			metrics.cpm !== null &&
			metrics.cpm !== 0
		) {
			const cpmValue =
				typeof metrics.cpm === "string"
					? parseFloat(metrics.cpm.replace(/[$,]/g, ""))
					: parseFloat(metrics.cpm);
			if (!isNaN(cpmValue) && cpmValue > 0) {
				cpm = `${(cpmValue / 1000000).toFixed(2)}`;
				cpmSource = "metrics.cpm";
			}
		}
		// Fallback 4: Search for any field with 'bid', 'cpm', 'cost' in name
		else {
			const cpmLikeFields = Object.keys(domain).filter(
				(key) =>
					(key.toLowerCase().includes("bid") ||
						key.toLowerCase().includes("cpm") ||
						key.toLowerCase().includes("cost") ||
						key.toLowerCase().includes("price")) &&
					domain[key] !== undefined &&
					domain[key] !== null &&
					domain[key] !== 0,
			);

			if (cpmLikeFields.length > 0) {
				const firstMatch = cpmLikeFields[0];
				const value = parseFloat(domain[firstMatch]);
				if (!isNaN(value) && value > 0) {
					cpm = `${(value / 1000000).toFixed(2)}`;
					cpmSource = `domain.${firstMatch}`;
				}
			}
		}

		return {
			domainName,
			cpm,
			cpmSource,
			relevance,
			originalDomain: domain,
		};
	};

	// Extract and sort domains by relevance in descending order
	const domainsWithData =
		setData?.domains
			?.map((domain) => {
				const extractedData = extractDomainData(domain);
				const marketRequest =
					domain.totalCprMarketRequests || domain.metrics?.marketRequests || 0;
				const impressions =
					domain.metrics?.impressions || domain.totalImpressions || 0;

				return {
					...extractedData,
					marketRequest,
					impressions,
					domain: domain,
					calculatedRelevance: extractedData.relevance,
				};
			})
			.sort((a, b) => b.relevance - a.relevance) || []; // Sort by relevance descending

	return (
		<div className="domains-tab" style={{ padding: "24px" }}>
			{/* Table Header */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 80px",
					gap: "16px",
					alignItems: "center",
					padding: "16px 8px",
					fontWeight: "500",
					color: "#374151",
					backgroundColor: "#f9fafb",
					borderBottom: "1px solid #e5e7eb",
					fontSize: "12px",
				}}
			>
				<div>Domain</div>
				<div>Relevance</div>
				<div>Market Request</div>
				<div>Impressions</div>
				<div>CPM</div>
				<div>Actions</div>
			</div>

			{/* Table Rows */}
			<div>
				{domainsWithData.map((domainData, index) => {
					return (
						<div
							key={domainData.domain.id || index}
							style={{
								display: "grid",
								gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 80px",
								gap: "16px",
								alignItems: "center",
								padding: "12px 8px",
								borderBottom: "1px solid #f3f4f6",
								backgroundColor: index % 2 === 0 ? "#f9fafb" : "transparent",
							}}
						>
							<div
								style={{
									fontSize: "13px",
									fontWeight: "400",
									color: "#1f2937",
								}}
							>
								{domainData.domainName}
							</div>
							<div
								style={{
									fontSize: "13px",
									fontWeight: "500",
									color: "#059669",
								}}
							>
								{domainData.calculatedRelevance}
							</div>
							<div
								style={{
									fontSize: "13px",
									fontWeight: "500",
									color: "#0891b2",
								}}
							>
								{domainData.marketRequest.toLocaleString()}
							</div>
							<div
								style={{
									fontSize: "13px",
									fontWeight: "500",
									color: "#7c3aed",
								}}
							>
								{domainData.impressions.toLocaleString()}
							</div>
							<div
								style={{
									fontSize: "13px",
									fontWeight: "500",
									color: domainData.cpm !== "N/A" ? "#7c3aed" : "#6b7280",
								}}
							>
								${domainData.cpm}
							</div>
							<div
								style={{
									width: "80px",
									textAlign: "center",
									flexShrink: 0,
								}}
							>
								<button
									onClick={() =>
										onViewDomain({
											...domainData.originalDomain,
											calculatedRelevance: domainData.calculatedRelevance,
										})
									}
									style={{
										background: "none",
										border: "none",
										cursor: "pointer",
										padding: "8px",
										borderRadius: "4px",
										transition: "all 0.2s ease",
										color: "#6b7280",
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
									}}
									onMouseEnter={(e) => {
										e.target.style.backgroundColor = "#f3f4f6";
										e.target.style.color = "#374151";
									}}
									onMouseLeave={(e) => {
										e.target.style.backgroundColor = "transparent";
										e.target.style.color = "#6b7280";
									}}
									title="View domain details"
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
										<circle cx="12" cy="12" r="3"></circle>
									</svg>
								</button>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

// Publishers Tab Component
// Publishers Tab Component
const PublishersTab = ({ setData }) => {
	// Group domains by individual publishers (split comma-separated values)
	const publisherData = setData.domains.reduce((acc, domain) => {
		const publisherField =
			domain.metrics?.publisher || domain.publisher || "Unknown Publisher";

		// Handle different publisher data formats
		let publisherList = [];

		if (Array.isArray(publisherField)) {
			// If it's already an array, use it directly
			publisherList = publisherField;
		} else if (typeof publisherField === "string") {
			// Split comma-separated publisher IDs/names
			publisherList = publisherField
				.split(",")
				.map((p) => p.trim())
				.filter((p) => p.length > 0);
		} else {
			publisherList = ["Unknown Publisher"];
		}

		// Count each individual publisher
		publisherList.forEach((publisher) => {
			if (!acc[publisher]) {
				acc[publisher] = {
					name: publisher,
					domains: 0,
					// Try to determine tier from publisher ID pattern or use default
					tier: publisher.length >= 9 ? "Premium" : "Standard",
				};
			}
			acc[publisher].domains++;
		});

		return acc;
	}, {});

	// Sort publishers by number of domains (descending)
	const publishers = Object.values(publisherData).sort(
		(a, b) => b.domains - a.domains,
	);

	return (
		<div className="publishers-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 2fr 1fr 1fr",
					gap: "16px",
					alignItems: "center",
					padding: "16px 8px",
					backgroundColor: "#f9fafb",
					borderBottom: "1px solid #e5e7eb",
					fontWeight: "500",
					color: "#374151",
					fontSize: "12px",
				}}
			>
				<div>Publisher ID</div>
				<div>No. of Domains</div>
				<div>Tier</div>
			</div>

			<div style={{ maxHeight: "400px", overflowY: "auto" }}>
				{publishers.map((publisher, index) => (
					<div
						key={index}
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 2fr 1fr 1fr",
							gap: "16px",
							alignItems: "center",
							padding: "12px 8px",
							borderBottom: "1px solid #f3f4f6",
							backgroundColor: index % 2 === 0 ? "#f9fafb" : "transparent",
						}}
					>
						<div
							style={{
								fontWeight: "400",
								color: "#1f2937",
								fontSize: "13px",
							}}
						>
							{publisher.name}
						</div>
						<div
							style={{
								fontWeight: "500",
								color: "#374151",
								fontSize: "13px",
							}}
						>
							{publisher.domains}
						</div>
						<div>
							<span
								style={{
									padding: "4px 8px",
									borderRadius: "12px",
									fontSize: "11px",
									fontWeight: "500",
									textTransform: "uppercase",
									backgroundColor:
										publisher.tier === "Premium" ? "#EBF4FA" : "#EEF9F6",
									color: "#000000",
								}}
							>
								{publisher.tier}
							</span>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// Categories Tab Component
const CategoriesTab = ({ setData }) => {
	// Group domains by individual categories from arrays
	const categoryData = setData.domains.reduce((acc, domain) => {
		const categories = domain.metrics?.categories ||
			domain.categories || ["Uncategorized"];

		// Handle both arrays and strings
		let categoryList;
		if (Array.isArray(categories)) {
			categoryList = categories;
		} else if (typeof categories === "string") {
			// If it's a string, split by comma and trim
			categoryList = categories.split(",").map((c) => c.trim());
		} else {
			categoryList = ["Uncategorized"];
		}

		// Count each individual category
		categoryList.forEach((category) => {
			const trimmedCategory = category.trim();
			if (trimmedCategory) {
				// Only count non-empty categories
				if (!acc[trimmedCategory]) {
					acc[trimmedCategory] = 0;
				}
				acc[trimmedCategory]++;
			}
		});

		return acc;
	}, {});

	const totalDomains = setData.domains.length;
	const categories = Object.entries(categoryData)
		.map(([name, count]) => ({
			name,
			count,
			percentage: ((count / totalDomains) * 100).toFixed(1),
		}))
		.sort((a, b) => b.count - a.count);

	return (
		<div className="categories-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "2fr 1fr 1fr 2fr",
					gap: "16px",
					alignItems: "center",
					padding: "16px 8px",
					backgroundColor: "#f9fafb",
					borderBottom: "1px solid #e5e7eb",
					fontWeight: "500",
					color: "#374151",
					fontSize: "12px",
				}}
			>
				<div>Category</div>
				<div>Number of Domains</div>
				<div>Percentage</div>
				<div>Distribution</div>
			</div>

			<div style={{ maxHeight: "400px", overflowY: "auto" }}>
				{categories.map((category, index) => (
					<div
						key={index}
						style={{
							display: "grid",
							gridTemplateColumns: "2fr 1fr 1fr 2fr",
							gap: "16px",
							alignItems: "center",
							padding: "12px 8px",
							borderBottom: "1px solid #f3f4f6",
							backgroundColor: index % 2 === 0 ? "#f9fafb" : "transparent",
						}}
					>
						<div
							style={{
								fontWeight: "400",
								color: "#1f2937",
								fontSize: "13px",
							}}
						>
							{category.name}
						</div>
						<div
							style={{
								fontWeight: "500",
								color: "#374151",
								fontSize: "13px",
							}}
						>
							{category.count}
						</div>
						<div
							style={{
								color: "#6b7280",
								fontSize: "13px",
							}}
						>
							{category.percentage}%
						</div>
						<div>
							<div
								style={{
									width: "100%",
									height: "8px",
									backgroundColor: "#e5e7eb",
									borderRadius: "4px",
									overflow: "hidden",
								}}
							>
								<div
									style={{
										height: "100%",
										backgroundColor: "#0076BD",
										transition: "width 0.3s ease",
										width: `${category.percentage}%`,
									}}
								></div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// Brand Safety Tab Component
const BrandSafetyTab = ({ setData }) => {
	const totalDomains = setData.domains.length;

	// Extract ALL actual brand safety tiers from domains (don't normalize to predefined categories)
	const safetyTierCounts = setData.domains.reduce((acc, domain) => {
		// Check multiple possible locations for safety tier data
		const safetyTier =
			domain.safetyTier ||
			domain.metrics?.safetyTier ||
			domain.brandSafety ||
			domain.metrics?.brandSafety ||
			domain.safety ||
			domain.metrics?.safety ||
			null;

		// Use the actual tier name from backend (don't normalize)
		let tierName = "Unknown";
		if (
			safetyTier &&
			typeof safetyTier === "string" &&
			safetyTier.trim() !== ""
		) {
			tierName = safetyTier.trim(); // Keep the original tier name
		} else if (safetyTier && typeof safetyTier !== "string") {
			tierName = String(safetyTier); // Convert to string if it's not already
		}

		if (!acc[tierName]) {
			acc[tierName] = 0;
		}
		acc[tierName]++;
		return acc;
	}, {});

	// Get all tier names and filter out 'Unknown' if it's the only tier
	const tierNames = Object.keys(safetyTierCounts);
	const hasActualSafetyData =
		tierNames.some((tier) => tier !== "Unknown") ||
		(tierNames.length === 1 &&
			tierNames[0] === "Unknown" &&
			safetyTierCounts["Unknown"] < totalDomains);

	if (!hasActualSafetyData && safetyTierCounts["Unknown"] === totalDomains) {
		return (
			<div className="brand-safety-tab" style={{ padding: "24px" }}>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						padding: "40px",
						textAlign: "center",
						color: "#6b7280",
					}}
				>
					<div style={{ fontSize: "48px", marginBottom: "16px" }}>🛡️</div>
					<h3
						style={{
							fontSize: "18px",
							fontWeight: "500",
							color: "#374151",
							margin: "0 0 8px 0",
						}}
					>
						No Brand Safety Data Available
					</h3>
					<p
						style={{
							fontSize: "14px",
							color: "#6b7280",
							margin: 0,
							maxWidth: "400px",
							lineHeight: "1.5",
						}}
					>
						Brand safety tier information is not available for the domains in
						this set. This data might not be included in the current dataset.
					</p>
				</div>
			</div>
		);
	}

	// Create safety data array with ALL actual backend tiers (sorted by domain count)
	const safetyData = Object.entries(safetyTierCounts)
		.map(([tier, domains]) => ({
			tier,
			domains,
		}))
		.sort((a, b) => b.domains - a.domains); // Sort by domain count (highest first)

	// Calculate percentages based on actual data
	const safetyDataWithPercentages = safetyData.map((item) => ({
		...item,
		percentage:
			totalDomains > 0 ? Math.round((item.domains / totalDomains) * 100) : 0,
	}));

	// Calculate overall safety score based on tier distribution
	// For unknown tiers, we'll assign weights based on common naming patterns
	const calculateTierWeight = (tierName) => {
		const lowerTier = tierName.toLowerCase();
		if (
			lowerTier.includes("universal") ||
			lowerTier.includes("high") ||
			lowerTier.includes("premium") ||
			lowerTier === "safe"
		) {
			return 1.0;
		} else if (
			lowerTier.includes("standard") ||
			lowerTier.includes("medium") ||
			lowerTier.includes("moderate") ||
			lowerTier.includes("general")
		) {
			return 0.7;
		} else if (
			lowerTier.includes("limited") ||
			lowerTier.includes("low") ||
			lowerTier.includes("restricted") ||
			lowerTier.includes("basic")
		) {
			return 0.3;
		} else {
			return 0.5; // Default weight for unknown tier names
		}
	};

	const safetyScore = Math.round(
		safetyDataWithPercentages.reduce((score, item) => {
			const weight = calculateTierWeight(item.tier);
			return score + item.percentage * weight;
		}, 0),
	);

	return (
		<div className="brand-safety-tab" style={{ padding: "24px" }}>
			<div
				style={{
					display: "flex",
					gap: "40px",
					alignItems: "flex-start",
				}}
			>
				{/* Safety Score Circle */}
				<div
					style={{
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<div
						style={{
							width: "120px",
							height: "120px",
							borderRadius: "50%",
							background: "#0076BD",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							color: "white",
						}}
					>
						<div
							style={{
								fontSize: "32px",
								fontWeight: "600",
								lineHeight: 1,
							}}
						>
							{safetyScore}
						</div>
						<div
							style={{
								fontSize: "11px",
								fontWeight: "400",
								textTransform: "uppercase",
								letterSpacing: "0.5px",
								marginTop: "4px",
							}}
						>
							Safety Score
						</div>
					</div>
				</div>

				{/* Safety Tiers Table */}
				<div
					style={{
						flex: 1,
						minWidth: 0,
					}}
				>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1.5fr 1fr 1fr 2fr",
							gap: "16px",
							alignItems: "center",
							padding: "16px 8px",
							backgroundColor: "#f9fafb",
							borderBottom: "1px solid #e5e7eb",
							fontWeight: "500",
							color: "#374151",
							fontSize: "12px",
							textTransform: "uppercase",
							letterSpacing: "0.5px",
						}}
					>
						<div>Safety Tier</div>
						<div>Domains</div>
						<div>Percentage</div>
						<div>Distribution</div>
					</div>

					<div
						style={{
							background: "white",
							maxHeight: "300px",
							overflowY: "auto",
						}}
					>
						{safetyDataWithPercentages.map((item, index) => (
							<div
								key={index}
								style={{
									display: "grid",
									gridTemplateColumns: "1.5fr 1fr 1fr 2fr",
									gap: "16px",
									alignItems: "center",
									padding: "16px 8px",
									borderBottom: "1px solid #f3f4f6",
								}}
							>
								<div
									style={{
										fontSize: "13px",
										fontWeight: "400",
									}}
								>
									<span
										style={{
											textDecoration: "underline",
											cursor: "pointer",
											fontWeight: "500",
											fontSize: "13px",
											color: "#000000",
										}}
									>
										{item.tier}
									</span>
								</div>
								<div>
									<span
										style={{
											color: "white",
											padding: "4px 12px",
											borderRadius: "20px",
											fontWeight: "500",
											fontSize: "12px",
											display: "inline-block",
											minWidth: "24px",
											textAlign: "center",
											backgroundColor: "#000000",
										}}
									>
										{item.domains}
									</span>
								</div>
								<div
									style={{
										fontWeight: "500",
										color: "#374151",
										fontSize: "13px",
									}}
								>
									{item.percentage}%
								</div>
								<div style={{ padding: "0 8px" }}>
									<div
										style={{
											width: "100%",
											height: "8px",
											backgroundColor: "#e5e7eb",
											borderRadius: "4px",
											overflow: "hidden",
										}}
									>
										<div
											style={{
												height: "100%",
												borderRadius: "4px",
												transition: "width 0.3s ease",
												width: `${item.percentage}%`,
												backgroundColor: "#0076BD",
											}}
										></div>
									</div>
								</div>
							</div>
						))}
					</div>

					{/* Show note about data source */}
					<div
						style={{
							marginTop: "16px",
							padding: "12px",
							backgroundColor: "#f0f9ff",
							border: "1px solid #bae6fd",
							borderRadius: "6px",
							fontSize: "12px",
							color: "#0369a1",
						}}
					>
						<strong>Data Source:</strong> All brand safety tiers extracted
						directly from domain metadata (
						{Object.keys(safetyTierCounts).length} unique tiers found). Safety
						score calculated using tier distribution and naming pattern
						analysis.
					</div>
				</div>
			</div>
		</div>
	);
};

// Individual Domain Details Modal Component
const IndividualDomainModal = ({ isOpen, onClose, domainData }) => {
	if (!isOpen || !domainData) return null;

	const domainName =
		domainData.name ||
		domainData.domain ||
		domainData.url ||
		domainData.hostname ||
		domainData;
	const metrics = domainData.metrics || domainData;

	let calculatedRelevance = "N/A";
	if (typeof domainData.score === "number") {
		calculatedRelevance = calculateRelevanceFromScore(domainData.score);
	} else if (typeof domainData.relevance === "number") {
		calculatedRelevance = Math.round(domainData.relevance);
	} else if (typeof domainData.relevanceScore === "number") {
		calculatedRelevance = Math.round(domainData.relevanceScore);
	} else if (typeof metrics.relevance === "number") {
		calculatedRelevance = Math.round(metrics.relevance);
	} else {
		calculatedRelevance = 75;
	}

	// Apply the same mapping logic as DomainsTab for CTR
	let formattedCTR = "N/A";
	if (metrics.rawCtr !== undefined) {
		const calculatedCTR = parseFloat(metrics.rawCtr);
		// Handle scientific notation like 0E-20 (which means 0)
		if (calculatedCTR === 0 || metrics.rawCtr.toString().includes("E-")) {
			formattedCTR = "0.00%";
		} else {
			formattedCTR = `${calculatedCTR.toFixed(4)}%`;
		}
	} else if (metrics.ctr) {
		// Fallback to original ctr field if rawCtr is not available
		formattedCTR = metrics.ctr;
	}

	// Format VTR properly
	let formattedVTR = "N/A";
	if (metrics.vtr !== undefined && metrics.vtr !== null) {
		if (typeof metrics.vtr === "string" && metrics.vtr.includes("%")) {
			formattedVTR = metrics.vtr;
		} else {
			const vtrValue = parseFloat(metrics.vtr);
			if (!isNaN(vtrValue)) {
				formattedVTR = `${vtrValue.toFixed(4)}%`;
			}
		}
	}

	// Format viewability properly
	let formattedViewability = "N/A";
	if (metrics.viewability !== undefined && metrics.viewability !== null) {
		if (
			typeof metrics.viewability === "string" &&
			metrics.viewability.includes("%")
		) {
			formattedViewability = metrics.viewability;
		} else {
			const viewabilityValue = parseFloat(metrics.viewability);
			if (!isNaN(viewabilityValue)) {
				formattedViewability = `${viewabilityValue.toFixed(1)}%`;
			}
		}
	}

	// Performance metrics
	const performanceMetrics = [
		{ label: "Viewability", value: formattedViewability, icon: "visibility" },
		{ label: "CTR", value: formattedCTR, icon: "trending_up" },
		{ label: "VTR", value: formattedVTR, icon: "play_circle" },
	];

	// Detail items with fixed reach formatting and all publishers
	const detailItems = [
		{
			label: "Categories",
			value: (() => {
				const cats = metrics.categories || domainData.categories;
				if (!cats || cats === "N/A") return "N/A";

				// If it's already an array, join with commas
				if (Array.isArray(cats)) {
					return cats.join(", ");
				}

				// If it's a string, split on capital letters and join with commas
				if (typeof cats === "string") {
					return cats.replace(/([a-z])([A-Z])/g, "$1, $2");
				}

				return cats;
			})(),
		},
		{
			label: "Publisher",
			value: (() => {
				// Fallback: check for publisheraccountid array
				const publisherIds = domainData.publisher || [];
				if (Array.isArray(publisherIds) && publisherIds.length > 0) {
					return publisherIds.join(", ");
				}
				if (typeof publisherIds === "string") {
					return publisherIds.match(/.{1,9}/g)?.join(", ") || publisher;
				}

				// Use the publisher from metrics which should already be formatted as comma-separated
				const publisher = metrics.publisher || domainData.publisher;
				if (publisher && publisher !== "N/A" && publisher !== "Unknown") {
					return publisher;
				}
				return "N/A";
			})(),
		},
		{
			label: "Reach",
			value: formatReachValue(metrics.reach || domainData.reach) || "N/A",
		},
		{ label: "Relevance", value: calculatedRelevance },
		{ label: "Clicks", value: metrics.clicks || domainData.clicks || "N/A" },
		{
			label: "Impressions",
			value: metrics.impressions || domainData.impressions || "N/A",
		},
	];

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div className="ddm-container" onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className="ddm-header">
					<h3 className="ddm-title">
						{typeof domainName === "string" ? domainName : "Unknown Domain"}
					</h3>
					<p className="ddm-subtitle">Domain performance metrics</p>
					<button onClick={onClose} className="ddm-close-btn">
						<XIcon />
					</button>
				</div>

				{/* Metrics Grid */}
				<div className="ddm-metrics-grid">
					{performanceMetrics.map((metric, index) => (
						<div key={index} className="ddm-metric-card">
							<div className="ddm-metric-value">{metric.value}</div>
							<div className="ddm-metric-label">{metric.label}</div>
						</div>
					))}
				</div>

				{/* Details Grid */}
				<div className="ddm-content">
					<div className="ddm-details-grid">
						{detailItems.map((item, index) => (
							<div key={index} className="ddm-detail-item">
								<div className="ddm-detail-label">{item.label}</div>
								<div className="ddm-detail-value">{item.value}</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

const DomainDetailsModal = ({
	isOpen,
	onClose,
	selectedSet,
	prebuiltDomainSets,
}) => {
	const [selectedDomain, setSelectedDomain] = useState(null);
	const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("overview");

	if (!isOpen || !selectedSet) return null;

	const setData = prebuiltDomainSets[selectedSet];

	// Tab configuration
	const tabs = [
		{ id: "overview", label: "Overview" },
		{ id: "domains", label: "Domains" },
		{ id: "publishers", label: "Publishers" },
		{ id: "categories", label: "Categories" },
		{ id: "brand-safety", label: "Brand Safety" },
	];

	// Function to handle eye icon click
	const handleViewDomain = (domain) => {
		setSelectedDomain(domain);
		setIsDomainModalOpen(true);
	};

	// Function to close individual domain modal
	const closeDomainModal = () => {
		setIsDomainModalOpen(false);
		setSelectedDomain(null);
	};

	// Render tab content
	const renderTabContent = () => {
		switch (activeTab) {
			case "overview":
				return <OverviewTab setData={setData} />;
			case "domains":
				return <DomainsTab setData={setData} onViewDomain={handleViewDomain} />;
			case "publishers":
				return <PublishersTab setData={setData} />;
			case "categories":
				return <CategoriesTab setData={setData} />;
			case "brand-safety":
				return <BrandSafetyTab setData={setData} />;
			default:
				return <OverviewTab setData={setData} />;
		}
	};

	return (
		<>
			<div className="modal-overlay" onClick={onClose}>
				<div
					className="modal-container tabbed-modal"
					onClick={(e) => e.stopPropagation()}
				>
					{/* Modal Header */}
					<div
						style={{
							position: "relative",
							padding: "24px 60px 24px 24px",
							borderBottom: "1px solid #e5e7eb",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div>
							<h2
								style={{
									fontSize: "20px",
									fontWeight: "600",
									color: "#1f2937",
									margin: "0 0 4px 0",
								}}
							>
								{setData.name}
							</h2>
							<p
								style={{
									fontSize: "14px",
									color: "#6b7280",
									margin: 0,
								}}
							>
								Top performing domains based on viewability
							</p>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "12px",
								marginRight: "40px",
							}}
						>
							<ExportDropdown setData={setData} />
							<button
								onClick={onClose}
								style={{
									position: "absolute",
									top: "16px",
									right: "16px",
									background: "none",
									border: "none",
									cursor: "pointer",
									padding: "8px",
									borderRadius: "4px",
									color: "#6b7280",
									transition: "all 0.2s ease",
									zIndex: 10,
								}}
							>
								<XIcon />
							</button>
						</div>
					</div>

					{/* Tab Navigation */}
					<TabNavigation
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						tabs={tabs}
					/>

					{/* Modal Content */}
					<div
						style={{
							padding: 0,
							maxHeight: "60vh",
							overflowY: "auto",
						}}
					>
						{renderTabContent()}
					</div>
				</div>
			</div>

			{/* Individual Domain Details Modal */}
			<IndividualDomainModal
				isOpen={isDomainModalOpen}
				onClose={closeDomainModal}
				domainData={selectedDomain}
			/>

			<style jsx>{`
        /* BASE MODAL STYLES */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
        }

        .modal-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* Enhanced Domain Modal with Tabs - Complete CSS */

        /* Modal container adjustments for tabbed interface */
        .tabbed-modal {
          max-width: 900px;
          width: 95%;
          max-height: 85vh;
        }

        /* Tab Navigation Styles */
        .tab-navigation {
          display: flex;
          border-bottom: 1px solid #e5e7eb;
          background-color: #f9fafb;
          overflow-x: auto;
        }

        .tab-button {
          background: none;
          border: none;
          padding: 16px 24px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 8px;
          border-bottom: 2px solid transparent;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .tab-button:hover {
          color: #374151;
          background-color: #f3f4f6;
        }

        .tab-button.active {
          color: #0076BD;
          border-bottom-color: #0076BD;
          background-color: white;
        }

        /* Tabbed content container */
        .tabbed-content {
          padding: 0;
          max-height: 60vh;
          overflow-y: auto;
        }

        /* INDIVIDUAL DOMAIN MODAL STYLES */
        .individual-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1001;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
        }

        .individual-domain-modal {
          max-width: 400px;
          width: 90%;
          max-height: 500px;
          position: relative;
          background: white;
          border-radius: 8px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          transform: scale(1);
          transition: all 0.2s ease;
        }

        .individual-modal-overlay.opening .individual-domain-modal {
          transform: scale(0.95);
          animation: modalAppear 0.2s ease forwards;
        }

        @keyframes modalAppear {
          from {
            opacity: 0;
            transform: scale(0.95);
          }

          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .individual-modal-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          position: relative;
        }

        .individual-modal-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          padding-right: 40px;
          /* Make space for close button */
        }

        .individual-modal-header .close-button {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          color: #6b7280;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
        }

        .individual-modal-header .close-button:hover {
          background-color: #f3f4f6;
          color: #374151;
        }

        .individual-domain-content {
          padding: 0;
          max-height: 400px;
          overflow-y: auto;
        }

        .domain-details-list {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .domain-details-list .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .domain-details-list .detail-label {
          font-weight: 700;
          font-size: 14px;
          color: #374151;
          margin: 0;
        }

        .domain-details-list .detail-value {
          font-size: 14px;
          color: #6b7280;
          margin: 0;
          word-break: break-word;
          line-height: 1.4;
        }

        .domain-details-list .detail-item:first-child .detail-label {
          color: #0076BD;
          font-size: 15px;
        }

        .domain-details-list .detail-item:first-child .detail-value {
          color: #0076BD;
          font-weight: 600;
          font-size: 15px;
        }

        /* Close button styles for main modal */
        .modal-header .close-button {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          color: #6b7280;
          transition: all 0.2s ease;
          z-index: 10;
        }

        .modal-header .close-button:hover {
          background-color: #f3f4f6;
          color: #374151;
        }

        /* Export button styles */
        .export-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: white;
          transition: all 0.2s ease;
        }

        .export-button:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .export-dropdown {
          position: relative;
          z-index: 10;
        }

        .dropdown-menu {
          animation: fadeIn 0.15s ease-out;
          border: 1px solid var(--openx-border);
          border-radius: 4px;
          background-color: white;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }

        .dropdown-item {
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
          color: var(--openx-text-primary);
          transition: background-color 0.15s ease;
        }

        .dropdown-item:hover {
          background-color: var(--openx-surface);
        }

        .dropdown-item:first-child {
          border-bottom: 1px solid var(--openx-border);
        }

        /* Filter Dropdown Styles */
        .filter-dropdown {
          position: relative;
          z-index: 10;
        }

        .filter-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 1px solid var(--openx-border);
          border-radius: 4px;
          background-color: white;
          cursor: pointer;
          font-size: 14px;
          color: var(--openx-text-primary);
          transition: all 0.2s ease;
        }

        .filter-button:hover {
          border-color: #0076BD;
          box-shadow: 0 0 0 3px rgba(0, 118, 189, 0.1);
        }

        /* Domains Filter Section */
        .domains-filter-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding: 12px;
          background-color: var(--openx-surface);
          border-radius: 4px;
          border: 1px solid var(--openx-border);
        }

        /* Animation for dropdown appearance */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Set card hover effects */
        .prebuild-sets-container [style*="cursor: pointer"]:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .tabbed-modal {
            width: 98%;
            max-height: 90vh;
          }

          .tab-button {
            padding: 12px 16px;
            font-size: 13px;
          }

          .export-button {
            padding: 6px 8px;
            font-size: 12px;
          }

          .filter-button {
            padding: 6px 8px;
            font-size: 12px;
          }

          .dropdown-item {
            padding: 6px 8px;
            font-size: 12px;
          }

          .domains-filter-section {
            flex-direction: column;
            gap: 8px;
            align-items: stretch;
          }

          .individual-domain-modal {
            width: 95%;
            max-width: 350px;
            max-height: 450px;
          }

          .domain-details-list {
            padding: 16px;
            gap: 14px;
          }

          .individual-modal-header {
            padding: 12px 16px;
          }

          .individual-modal-title {
            font-size: 16px;
          }
        }

        /* Domain Details Modal - Compact & Clean */
        .ddm-container {
          max-width: 500px;
          width: 95%;
          max-height: 80vh;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .ddm-header {
          padding: 24px 24px 16px;
          background: white;
          border-bottom: 1px solid #f1f5f9;
          position: relative;
        }

        .ddm-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 8px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ddm-title::before {
          content: "";
          display: block;
          width: 4px;
          height: 20px;
          background: #3b82f6;
          border-radius: 2px;
        }

        .ddm-subtitle {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        .ddm-close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: #f8fafc;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: 6px;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .ddm-close-btn:hover {
          background: #e2e8f0;
          color: #1e40af;
        }

        /* Metrics Grid */
        .ddm-metrics-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          padding: 0 24px 16px;
        }

        .ddm-metric-card {
          background: #f8fafc;
          border-radius: 8px;
          padding: 12px;
          text-align: center;
          border: 1px solid #e2e8f0;
        }

        .ddm-metric-value {
          font-size: 18px;
          font-weight: 700;
          color: #1e40af;
          margin-bottom: 2px;
        }

        .ddm-metric-label {
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }

        /* Details List */
        .ddm-content {
          padding: 0 24px 24px;
          overflow-y: auto;
        }

        .ddm-details-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .ddm-detail-item {
          background: white;
          border-radius: 8px;
          padding: 12px;
          border: 1px solid #f1f5f9;
        }

        .ddm-detail-label {
          font-size: 12px;
          color: #64748b;
          margin-bottom: 4px;
          font-weight: 500;
        }

        .ddm-detail-value {
          font-size: 13px;
          color: #1e293b;
          font-weight: 400;
        }

        /* Responsive */
        @media (max-width: 480px) {
          .ddm-metrics-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .ddm-details-grid {
            grid-template-columns: 1fr;
          }
          
          .ddm-header {
            padding: 20px 20px 12px;
          }
          
          .ddm-content {
            padding: 0 20px 20px;
          }
        }
      `}</style>
		</>
	);
};

export default DomainDetailsModal;
